import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import LinearProgress from '@material-ui/core/LinearProgress';
import { authLogout, setAgencySuccess } from '@actions/auth-actions';
import { fetchDataTypes } from '@actions/data-types-actions';
import { pollUnreadNotificationsAsync } from '@actions/notifications-actions';
import { fetchConfigUpdate } from '@components/config/actions';
import { paper } from '@constants/mui-theme';
import { dataTypesLoaderSelector } from '@selectors/data-types-selector';
import Header from '@components/header';
import { getAgencyId } from '@utils/permission-utils';
import ActionConfirmationDialog from './confirmation-dialog';
import ErrorSnackbar from './error-snackbar';
import InfoSnackbar from './info-snackbar';
import useIdleMonitor from './idle-monitor';
import Disclaimer from './disclaimer';
import AgencySelect from './agency-select';
import WarningDialog from './warning-dialog';
import SomFooter from '@shared/som-footer';
import './authenticated.scss';

const AuthenticatedLayout = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loader = useSelector(dataTypesLoaderSelector);

  // Handle idle logout
  const onIdle = useCallback(() => {
    dispatch(authLogout(location));
  }, [dispatch, location]);

  // Use idle monitor hook
  useIdleMonitor(onIdle);

  // Initialize on mount
  useEffect(() => {
    dispatch(fetchConfigUpdate());
    dispatch(fetchDataTypes());
    dispatch(setAgencySuccess(getAgencyId()));
    window.stopPolling = false;
    dispatch(pollUnreadNotificationsAsync());

    // Cleanup on unmount
    return () => {
      window.stopPolling = true;
    };
  }, [dispatch]);

  const { count, total } = loader;
  const loaded = count === total;

  return (
    <div styleName="app-container">
      <a href="#main-content" styleName="skip-nav">
        Skip to main content
      </a>
      <header
        role="banner"
        styleName="header-container"
        style={{
          ...paper,
          boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px'
        }}
      >
        <Header />
      </header>
      <main role="main" id="main-content" styleName="content-container">
        {loaded
          ? children
          : <LinearProgress variant="determinate" value={(count / total) * 100} />}
      </main>
      <InfoSnackbar />
      <ErrorSnackbar />
      <ActionConfirmationDialog />
      <WarningDialog />
      <AgencySelect />
      <Disclaimer />
      <SomFooter />
    </div>
  );
};

AuthenticatedLayout.propTypes = {
  children: PropTypes.element
};

export default AuthenticatedLayout;
